import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ShieldCheck, Receipt, Sparkles, ArrowRight, Search, Send, KeyRound } from "lucide-react";
import SearchBar from "@/components/SearchBar";
import ListingCard from "@/components/ListingCard";
import { useLang } from "@/lib/i18n";
import { api } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1724582586529-62622e50c0b3?crop=entropy&cs=srgb&fm=jpg&q=80&w=2000";

function Hero() {
  const { t } = useLang();
  return (
    <section className="relative overflow-hidden bg-[#0B192C]">
      <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B192C] via-[#0B192C]/60 to-transparent" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-14 pb-10 sm:pt-24 sm:pb-16">
        <span className="rise inline-flex rounded-full bg-[#FF6B00] px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-white" data-testid="hero-badge">{t("hero_badge")}</span>
        <h1 className="rise rise-1 mt-5 max-w-3xl text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
          {t("hero_title_1")} <span className="text-[#FF6B00]">{t("hero_title_2")}</span>
        </h1>
        <p className="rise rise-2 mt-5 max-w-2xl text-base md:text-lg text-slate-200">{t("hero_sub")}</p>
        <div className="rise rise-3 mt-8 rounded-2xl bg-white p-3 sm:p-4 shadow-2xl"><SearchBar /></div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const { t } = useLang();
  const steps = [[Search, "how_t1", "how_d1"], [Send, "how_t2", "how_d2"], [KeyRound, "how_t3", "how_d3"]];
  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
      <h2 className="text-2xl sm:text-3xl font-bold text-[#0B192C]">{t("how_title")}</h2>
      <div className="mt-8 grid gap-4 md:grid-cols-3">
        {steps.map(([Icon, a, b], i) => (
          <div key={a} className="rounded-2xl bg-white border border-slate-200 p-6" data-testid={`how-step-${i + 1}`}>
            <div className="flex items-center gap-3">
              <span className="grid h-11 w-11 place-items-center rounded-xl bg-orange-50 text-[#FF6B00]"><Icon className="h-5 w-5" /></span>
              <span className="font-display text-4xl font-extrabold text-slate-200">0{i + 1}</span>
            </div>
            <h3 className="mt-4 text-xl font-semibold text-[#0B192C]">{t(a)}</h3>
            <p className="mt-2 text-sm text-slate-600">{t(b)}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Featured() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/listings", { params: { limit: 6 } }).then((r) => setItems(r.data)); }, []);
  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
      <div className="flex items-end justify-between gap-4">
        <h2 className="text-2xl sm:text-3xl font-bold text-[#0B192C]">{t("featured")}</h2>
        <Link to="/search" className="inline-flex items-center gap-1 text-sm font-bold text-[#FF6B00] hover:gap-2 transition-[gap]" data-testid="featured-see-all">{t("see_all")}<ArrowRight className="h-4 w-4" /></Link>
      </div>
      <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="featured-grid">
        {items.map((l) => <ListingCard key={l.listing_id} l={l} />)}
      </div>
    </section>
  );
}

function Audiences() {
  const { t } = useLang();
  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24 grid gap-5 lg:grid-cols-[1.2fr_1fr]">
      <div className="rounded-3xl bg-[#142B5C] p-8 sm:p-10 text-white">
        <h2 className="text-2xl sm:text-3xl font-bold">{t("owners_title")}</h2>
        <p className="mt-4 text-slate-200">{t("owners_sub")}</p>
        <div className="mt-6 grid grid-cols-3 gap-3 text-center">
          {[["100%", "€1.000"], ["-5%", "€50"], ["95%", "€950"]].map(([a, b], i) => (
            <div key={a} className="rounded-xl bg-white/10 p-3">
              <p className="text-xs uppercase tracking-wider text-slate-300">{[t("your_price"), t("fee5"), t("net")][i]}</p>
              <p className="mt-1 font-display text-xl font-extrabold text-[#FF6B00]">{b}</p>
            </div>
          ))}
        </div>
        <Link to="/register?role=owner" className="mt-8 inline-flex h-12 items-center gap-2 rounded-full bg-[#FF6B00] px-6 font-bold hover:bg-[#E05A00] transition-colors" data-testid="owners-cta-btn">{t("owners_cta")}<ArrowRight className="h-4 w-4" /></Link>
      </div>
      <div className="rounded-3xl bg-white border border-slate-200 p-8 sm:p-10">
        <h2 className="text-2xl sm:text-3xl font-bold text-[#0B192C]">{t("companies_title")}</h2>
        <p className="mt-4 text-slate-600">{t("companies_sub")}</p>
        <ul className="mt-6 space-y-3">
          {[[Receipt, "b1"], [Sparkles, "b2"], [ShieldCheck, "b3"]].map(([I, k]) => (
            <li key={k} className="flex items-center gap-3 text-sm font-semibold text-[#142B5C]"><I className="h-5 w-5 text-[#FF6B00]" />{t(k)}</li>
          ))}
        </ul>
        <Link to="/register?role=tenant" className="mt-8 inline-flex h-12 items-center gap-2 rounded-full border-2 border-[#142B5C] px-6 font-bold text-[#142B5C] hover:bg-[#142B5C] hover:text-white transition-colors" data-testid="companies-cta-btn">{t("companies_cta")}</Link>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <Featured />
      <HowItWorks />
      <Audiences />
    </div>
  );
}
